import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "../api/baseQuery";
import { getStoredToken } from "../utils/impersonation.utils";

export type CallDashboardStats = {
  total_calls: number;
  urgent_calls: number;
  new_patients: number;
  existing_patients: number;
  /** Calls per skill code in the selected window, for the counts on each chip. */
  skill_counts: Record<string, number>;
  /** What the AI receptionist did in the selected window. */
  actions?: CallActionCount[];
};

export type CallActionCount = { code: string; label: string; count: number };

/** One thing the AI receptionist did on a call; "attention" needs a call back. */
export type CallAction = {
  code: string;
  label: string;
  tone: "success" | "attention";
};

export type ReceptionistStatus = {
  /** Every feature a practice's receptionist can have, in display order. */
  features: { code: string; label: string }[];
  agents: {
    agent_identifier: string;
    platform: string;
    is_enabled: boolean;
    features: Record<string, boolean>;
  }[];
  /** Clinic numbers behind the Twilio front door, and their health. */
  phone_lines?: {
    number: string;
    is_active: boolean;
    /** The AI platform answering first right now. */
    answering: string;
    standby: string;
    /** Answering on its standby platform right now (flipped). */
    on_standby: boolean;
    canary_enabled: boolean;
    last_canary_ok: boolean | null;
    last_canary_at: string | null;
    last_flip_at: string | null;
    flip_reason: string;
  }[];
};

export type CallDashboardDateRange = "all_time" | "today" | "yesterday" | "last_7_days";

export type CallDashboardQueryArgs = {
  page?: number;
  /** Day filter, resolved server-side against `tz`. */
  range?: CallDashboardDateRange;
  /** IANA timezone the day filter is measured in. */
  tz?: string;
  /** Skill code to filter by, applied across all pages. */
  skill?: string;
};

export type CallSkill = {
  id: number;
  code: string;
  label: string;
  /**
   * Analysis field a rule-driven skill matches on (e.g. "provider"). Empty for the
   * built-in skills. Chips are grouped into their own labelled row per source_field.
   */
  source_field?: string;
};

export type CallDashboardCall = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  date: string; // ISO timestamp
  // New fields
  duration: number | null;
  summary: string | null;
  from_number: string | null;
  skills: CallSkill[];
  /** Latest insurance eligibility check made during this call, if any. */
  eligibility: {
    status: "success" | "rejected" | "error" | "pending";
    payer: string;
    plan: string;
    copay: string;
    mismatch: string;
    summary: string;
    checked_at: string;
  } | null;
  actions?: CallAction[];
};

export type PaginationInfo = {
  count: number;
  next: string | null;
  previous: string | null;
  page_size: number;
};

export type CallDashboardData = {
  calls: CallDashboardCall[];
  stats: CallDashboardStats;
  pagination: PaginationInfo;
};

export type CallSkillsResponse = {
  skills: CallSkill[];
};

export type CallTranscriptResponse = {
  id: number;
  transcript: string;
  call_identifier: string | null;
};

export type OfficeMessage = {
  id: number;
  /** The clinic line a voicemail came in on; empty for a message the AI took. */
  line: string;
  kind: "voicemail" | "agent_message";
  reason: string;
  caller_name: string;
  caller_number: string;
  call_identifier: string;
  duration_s: number;
  transcript: string;
  /** A voicemail recording exists; play it through {@link playOfficeRecording}. */
  has_recording: boolean;
  status: "open" | "done";
  created_at: string;
};

export const callsApi = createApi({
  reducerPath: "callsApi",
  tagTypes: ["CallDashboard", "OfficeMessages"],
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCallDashboardData: build.query<CallDashboardData, CallDashboardQueryArgs>({
      query: ({ page = 1, range = "all_time", tz, skill } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page, range, tz, skill },
      }),
      providesTags: [{ type: "CallDashboard", id: "DATA" }],
    }),
    getArchivedCallDashboardData: build.query<CallDashboardData, CallDashboardQueryArgs>({
      query: ({ page = 1, range = "all_time", tz, skill } = {}) => ({
        url: `/ai_admin/calls/dashboard/`,
        params: { page, range, tz, skill, archived: true },
      }),
      providesTags: [{ type: "CallDashboard", id: "ARCHIVED" }],
    }),
    archiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/archive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    unarchiveCall: build.mutation<{ status: string }, number>({
      query: (callId) => ({
        url: `/ai_admin/calls/${callId}/unarchive/`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "CallDashboard", id: "DATA" },
        { type: "CallDashboard", id: "ARCHIVED" },
      ],
    }),
    getCallSkills: build.query<CallSkillsResponse, void>({
      query: () => `/ai_admin/skills/`,
    }),
    getCallTranscript: build.query<CallTranscriptResponse, number>({
      query: (callId) => `/ai_admin/calls/${callId}/transcript/`,
    }),
    // Messages for the office: voicemails Twilio took when no AI platform answered,
    // and messages the AI took when its tools were failing.
    getOfficeMessages: build.query<{ messages: OfficeMessage[] }, void>({
      query: () => `/ai_admin/messages/?status=open`,
      providesTags: [{ type: "OfficeMessages", id: "OPEN" }],
    }),
    markOfficeMessageDone: build.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/ai_admin/messages/${id}/done/`, method: "POST" }),
      invalidatesTags: [{ type: "OfficeMessages", id: "OPEN" }],
    }),
    getReceptionistStatus: build.query<ReceptionistStatus, void>({
      query: () => `/ai_admin/receptionist/status/`,
    }),
  }),
});

export const {
  useGetCallDashboardDataQuery,
  useGetArchivedCallDashboardDataQuery,
  useArchiveCallMutation,
  useUnarchiveCallMutation,
  useGetCallSkillsQuery,
  useGetCallTranscriptQuery,
  useGetOfficeMessagesQuery,
  useMarkOfficeMessageDoneMutation,
  useGetReceptionistStatusQuery,
} = callsApi;

/**
 * Play a voicemail. The audio is fetched through the backend with the user's
 * token (Twilio's own URL needs account credentials and is never sent to the
 * browser), then played from memory.
 */
export async function playOfficeRecording(messageId: number): Promise<boolean> {
  const token = getStoredToken();
  const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/ai_admin/messages/${messageId}/recording/`, {
    headers: token ? { authorization: `Token ${token}` } : {},
  });
  if (!response.ok) return false;
  const url = URL.createObjectURL(await response.blob());
  const audio = new Audio(url);
  audio.onended = () => URL.revokeObjectURL(url);
  await audio.play();
  return true;
}
